// ============================================================================
// Pricing & markup math
// ============================================================================
// All money internally is INTEGER CENTS. UI converts to whole dollars for
// display via fmtUsd. Never use floats for money.
//
// Markup model (from CLAUDE.md hiring spec):
//   - Creator sets their rate ("net") in roster_creators.video_prices
//   - BMC adds 10% on top → that's the gross rate brands see
//   - If usage rights enabled, gross gets bumped by an additional % (default 40)
//   - Brand pays gross via Whop. Creator gets net via Trolley. BMC keeps the diff.
// ============================================================================

export const BMC_FEE_PCT = 10 // BMC platform fee, applied on top of creator net
export const DEFAULT_USAGE_RIGHTS_PCT = 40 // Default usage-rights bump on top of gross

// ============================================================================
// GMV display sentinels
// ============================================================================
// TikTok Partner Center reports a creator's 28-day GMV as a single number,
// EXCEPT when the creator has set their sales data to private — then it only
// exposes a coarse range, which our ingest stores as the exact value $2,500.
// So `creator_gmv === 2500` is a sentinel meaning "private — range only", not
// a literal $2.5K. UI shows a "Range" label + GMV_RANGE_TOOLTIP instead of a
// dollar figure for these creators (they are still shown in the roster).
// ($0 is a separate sentinel for "no sales data at all" and stays hidden.)
export const GMV_RANGE_SENTINEL_DOLLARS = 2500
export const GMV_RANGE_TOOLTIP =
  'Some creators have sales data set to private in TikTok, so Partner Center ' +
  'only shows a GMV range. Typically, creators are on the lower end of this ' +
  'range but not always'

export interface PriceQuote {
  /** Creator net (cents) — what they receive via Trolley */
  creatorNetCents: number
  /** Brand gross (cents) — what they pay via Whop */
  brandGrossCents: number
  /** BMC fee kept (cents) = gross - net */
  bmcFeeCents: number
  /** Whether usage rights bump was applied */
  usageRights: boolean
  /** Usage rights bump pct used (0 if usageRights=false) */
  usageRightsPct: number
}

/**
 * Quote a hire price. All inputs and outputs in cents.
 *
 * @param creatorNetCents what the creator should net (their stated rate)
 * @param usageRights whether brand wants usage rights
 * @param usageRightsPct optional override of default 40% bump
 */
export function quoteHire(
  creatorNetCents: number,
  usageRights: boolean = false,
  usageRightsPct: number = DEFAULT_USAGE_RIGHTS_PCT
): PriceQuote {
  if (creatorNetCents <= 0) {
    throw new Error('Creator rate must be positive')
  }

  // Apply BMC markup first
  const platformGross = Math.round(creatorNetCents * (1 + BMC_FEE_PCT / 100))

  // Then apply usage rights bump on top of gross (so usage rights also flow some uplift to creator)
  // Wait — re-read spec: "usage rights typically they will have to bid higher to get a creator to accept this"
  // So usage rights bump should partly go to creator (incentive to accept) and partly to BMC.
  // Simplest model: usage rights bumps both creator net and BMC fee proportionally.
  // i.e. multiply both creator net AND BMC fee by (1 + usageRightsPct/100)
  let finalCreatorNet = creatorNetCents
  let finalGross = platformGross
  let appliedUsagePct = 0

  if (usageRights) {
    appliedUsagePct = usageRightsPct
    const multiplier = 1 + usageRightsPct / 100
    finalCreatorNet = Math.round(creatorNetCents * multiplier)
    finalGross = Math.round(platformGross * multiplier)
  }

  return {
    creatorNetCents: finalCreatorNet,
    brandGrossCents: finalGross,
    bmcFeeCents: finalGross - finalCreatorNet,
    usageRights,
    usageRightsPct: appliedUsagePct,
  }
}

/**
 * Reverse: brand sets a custom gross they want to pay, work back to creator net.
 * Used when brand overrides displayed rate with a custom amount.
 */
export function quoteHireFromGross(
  brandGrossCents: number,
  usageRights: boolean = false,
  usageRightsPct: number = DEFAULT_USAGE_RIGHTS_PCT
): PriceQuote {
  if (brandGrossCents <= 0) {
    throw new Error('Brand rate must be positive')
  }

  // Reverse the formula: gross = net * (1 + fee%) * (1 + usage%)
  let divisor = 1 + BMC_FEE_PCT / 100
  let appliedUsagePct = 0
  if (usageRights) {
    appliedUsagePct = usageRightsPct
    divisor *= 1 + usageRightsPct / 100
  }

  const creatorNetCents = Math.round(brandGrossCents / divisor)

  return {
    creatorNetCents,
    brandGrossCents,
    bmcFeeCents: brandGrossCents - creatorNetCents,
    usageRights,
    usageRightsPct: appliedUsagePct,
  }
}

// ============================================================================
// Money formatting
// ============================================================================

/** Format integer cents as USD with $ symbol. Whole dollars only by default. */
export function fmtUsd(cents: number, opts: { showCents?: boolean } = {}): string {
  const sign = cents < 0 ? '-' : ''
  const abs = Math.abs(cents)
  if (opts.showCents) {
    // toLocaleString (not toFixed) so 4+ digit amounts still get thousands
    // separators — toFixed(2) alone rendered $1234.56 with no comma.
    return `${sign}$${(abs / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }
  return `${sign}$${Math.round(abs / 100).toLocaleString()}`
}
export function fmtGmv(s: string | null | undefined): string {
  if (!s) return '—'
  const n = parseFloat(s)
  if (!isFinite(n) || n <= 0) return '—'
  // K/M abbreviation with one decimal of precision — so 2500 → $2.5K, not
  // $3K. Trailing ".0" is stripped (3000 → $3K, 1_000_000 → $1M).
  const abbr = (v: number, suffix: string) =>
    `$${v.toFixed(1).replace(/\.0$/, '')}${suffix}`
  if (n >= 1_000_000) return abbr(n / 1_000_000, 'M')
  if (n >= 1_000) return abbr(n / 1_000, 'K')
  return `$${Math.round(n).toLocaleString()}`
}

/** fmtUsd, but cents render only when the amount actually has cents:
 *  $2,750 not $2750.00 — yet $522.50 keeps its .50. For money-moment
 *  surfaces (offers, hire confirmations) per Mike's display rule. */
export function fmtUsdAuto(cents: number): string {
  return fmtUsd(cents, { showCents: cents % 100 !== 0 })
}

/** Convert dollar input from UI to cents for storage */
export function dollarsToCents(dollars: number | string): number {
  const n = typeof dollars === 'string' ? parseFloat(dollars) : dollars
  if (isNaN(n)) return 0
  return Math.round(n * 100)
}

// ============================================================================
// roster_creators.video_prices JSONB helpers
// ============================================================================
// Two formats exist in the DB. All helpers accept `unknown` and normalize first.
//
// Format A — MongoDB import (old):
//   RawVideoPrice[]  — flat array, price in DOLLARS
//   e.g. [{ count: 10, price: 150, price_with_rights: 200 }]
//
// Format B — mobile app signup (new):
//   { tiers: [...], source: 'mobile_app', updated_at: '...' }
//   where each tier has price_cents / price_with_rights_cents in CENTS
//   e.g. { tiers: [{ count: 10, price_cents: 15000, price_with_rights_cents: 21000 }] }
//
// normalizeVideoPrices() collapses both to RawVideoPrice[] with price in DOLLARS.

export interface RawVideoPrice {
  count: number
  price: number  // DOLLARS (no-rights / standard rate)
  price_with_rights?: number | null  // DOLLARS, optional
  duration?: string
  durationCount?: number
}

export function normalizeVideoPrices(raw: unknown): RawVideoPrice[] | null {
  if (!raw) return null
  // Format A: flat array
  if (Array.isArray(raw)) return raw as RawVideoPrice[]
  // Format B: { tiers: [...] }
  const obj = raw as { tiers?: unknown[] }
  if (Array.isArray(obj.tiers)) {
    return obj.tiers.map((t: any) => ({
      count: t.count,
      price: t.price_cents / 100,
      price_with_rights: t.price_with_rights_cents != null ? t.price_with_rights_cents / 100 : null,
    }))
  }
  return null
}

/** Standard video package sizes brands can offer */
export const PACKAGE_SIZES = [1, 3, 5, 10, 20, 30] as const
export type PackageSize = typeof PACKAGE_SIZES[number]

/**
 * The package sizes a campaign accepts applications at
 * (cr_campaigns.allowed_package_sizes, added 2026-08-20).
 *
 * NULL / empty / all-garbage reads as "no restriction" — every standard size.
 * A campaign row written before the column existed, or by a client that doesn't
 * know about it, must keep offering everything; the one thing this must never
 * do is turn a missing preference into "this brand accepts nothing".
 * Mirrors creator-app components/jobs/types.ts getAllowedPackageSizes().
 */
export function normalizeAllowedPackageSizes(raw: unknown): PackageSize[] {
  if (!Array.isArray(raw)) return [...PACKAGE_SIZES]
  const picked = PACKAGE_SIZES.filter(size => raw.some(v => Number(v) === size))
  return picked.length ? picked : [...PACKAGE_SIZES]
}

/**
 * Reference tier to scale from when the requested package size has no
 * explicit rate stored — most commonly a 1- or 3-video package on a
 * video_prices row written before those tiers existed (added 2026-09-01).
 * Prefers the 10-pack, the canonical unit every rate-setting flow anchors on
 * (rate-setup.tsx, PublicSetRates.tsx), and falls back to whatever tier IS
 * set so a creator missing a 10-pack still resolves.
 */
function referenceTier(prices: RawVideoPrice[]): RawVideoPrice | null {
  return prices.find(p => p.count === 10 && p.price > 0)
    ?? prices.find(p => p.price > 0)
    ?? null
}

/**
 * Read a creator's stated rate for a given package size, in cents.
 * Returns null if the creator hasn't set a rate for that package
 * (price = 0 in raw data also counts as unset).
 *
 * Falls back to a linear per-video derivation from the reference tier when
 * this exact size isn't stored — same math buildVideoPrices uses when
 * writing a fresh row — so a legacy row saved before a tier existed (e.g.
 * 1/3-video packages) still resolves to a number instead of "not listed".
 */
export function getCreatorRateCents(
  videoPrices: unknown,
  packageSize: PackageSize
): number | null {
  const prices = normalizeVideoPrices(videoPrices)
  if (!prices) return null
  const match = prices.find(p => p.count === packageSize)
  if (match && match.price > 0) return dollarsToCents(match.price)
  const ref = referenceTier(prices)
  if (!ref) return null
  return dollarsToCents((ref.price / ref.count) * packageSize)
}

/** Quick check: does the creator have ANY rates set at all? */
export function hasAnyRates(videoPrices: unknown): boolean {
  const prices = normalizeVideoPrices(videoPrices)
  if (!prices) return false
  return prices.some(p => p.price && p.price > 0)
}

// ============================================================================
// Legacy import detection
// ============================================================================
// The legacy roster import seeded ~5K creators with a default $50 10-pack
// rate. They never actually set those rates themselves. We treat those as
// "no rates" everywhere — UI hides the per-video price and brands have to
// provide a custom rate when sending an offer.
//
// Signal: 10-pack price is exactly $50 in DOLLARS. Real creators who set $50
// themselves are rare (verified via SQL: 5,023 creators at $50 vs ~7K with
// other rates). False positives can re-set rates via the /set-rates magic link.
// ============================================================================

const IMPORTED_DEFAULT_TEN_PACK_DOLLARS = 50

/**
 * Returns true if the creator's rates look like the legacy import default
 * (currently: 10-pack price === $50 exactly). Use this to gate display of
 * "real" rates in roster / offer flows.
 */
export function isImportedDefaultRates(videoPrices: unknown): boolean {
  const prices = normalizeVideoPrices(videoPrices)
  if (!prices) return false
  const tenPack = prices.find(p => p.count === 10)
  if (!tenPack) return false
  return tenPack.price === IMPORTED_DEFAULT_TEN_PACK_DOLLARS
}

/**
 * Like hasAnyRates(), but returns false for creators whose only rates are
 * the legacy import default. Use this to drive UI ("does this creator have
 * REAL self-set rates?").
 */
export function hasSelfSetRates(videoPrices: unknown): boolean {
  if (!hasAnyRates(videoPrices)) return false
  // if (isImportedDefaultRates(videoPrices)) return false
  return true
}

/**
 * Read a creator's with-rights rate (cents) for a given package size.
 *
 * Resolution order:
 *   1. If the creator explicitly set `price_with_rights` for this package, use it.
 *   2. Else, if a base `price` exists for this package, fall back to
 *      `price * (1 + DEFAULT_USAGE_RIGHTS_PCT/100)` = `price * 1.4`.
 *   3. Else return null (creator has no rate at all for this package).
 *
 * Returns the **creator-net** with-rights rate in cents.
 *
 * When this exact size isn't stored at all (e.g. a 1/3-video package on a
 * legacy row), derives per-video from the reference tier instead of
 * returning null — preferring that tier's own explicit with-rights rate over
 * the flat fallback bump, same precedence as the matched-tier case above.
 */
export function getWithRightsRateCents(
  videoPrices: unknown,
  packageSize: PackageSize,
  fallbackPct: number = DEFAULT_USAGE_RIGHTS_PCT
): number | null {
  const prices = normalizeVideoPrices(videoPrices)
  if (!prices) return null
  const match = prices.find(p => p.count === packageSize)

  if (match) {
    // Explicit with-rights rate set — use it.
    if (match.price_with_rights != null && match.price_with_rights > 0) {
      return dollarsToCents(match.price_with_rights)
    }
    // Fall back to base rate × (1 + fallback%)
    if (match.price && match.price > 0) {
      const multiplier = 1 + fallbackPct / 100
      return dollarsToCents(match.price * multiplier)
    }
    return null
  }

  const ref = referenceTier(prices)
  if (!ref) return null
  const perVideoWithRights = ref.price_with_rights != null && ref.price_with_rights > 0
    ? ref.price_with_rights / ref.count
    : (ref.price / ref.count) * (1 + fallbackPct / 100)
  return dollarsToCents(perVideoWithRights * packageSize)
}

/**
 * Helper for UIs that need to show a suggested with-rights rate as a
 * placeholder/hint when the creator hasn't set one yet. Returns the
 * suggested DOLLAR amount (not cents) to drop into an input placeholder.
 *
 * Returns null if there's no base rate to extrapolate from.
 */
export function suggestWithRightsDollars(
  videoPrices: unknown,
  packageSize: PackageSize,
  fallbackPct: number = DEFAULT_USAGE_RIGHTS_PCT
): number | null {
  const prices = normalizeVideoPrices(videoPrices)
  if (!prices) return null
  const match = prices.find(p => p.count === packageSize)
  if (!match || !match.price || match.price <= 0) return null
  return Math.round(match.price * (1 + fallbackPct / 100))
}
